import { apiClient } from './api';

// Image generation provider types
export interface ImageProvider {
  name: string;
  models: ImageModel[];
}

export interface ImageModel {
  modelId: string;
  displayName: string;
  description: string;
  supportedSizes: ImageSize[];
  capabilities: ImageModelCapabilities;
  pricing?: ImageModelPricing;
}

export interface ImageSize {
  width: number;
  height: number;
  aspectRatio: string;
}

export interface ImageModelCapabilities {
  supportsNegativePrompt: boolean;
  supportsSeed: boolean;
  supportsStyle: boolean;
  supportsGuidanceScale: boolean;
  supportsInpainting: boolean;
  supportsOutpainting: boolean;
  supportsVariations: boolean;
  maxPromptLength: number;
  supportedStyles?: string[];
}

export interface ImageModelPricing {
  pricePerImage: number;
  currency: string;
}

export interface ImageGenerationRequest {
  model: string;
  prompt: string;
  negativePrompt?: string;
  width?: number;
  height?: number;
  numImages?: number;
  seed?: number;
  guidanceScale?: number;
  style?: string;
  additionalParameters?: Record<string, any>;
}

export interface ImageGenerationResponse {
  model: string;
  images: GeneratedImage[];
  metadata: Record<string, any>;
}

export interface GeneratedImage {
  url: string;
  contentType: string;
  metadata: Record<string, any>;
}

export interface ProviderValidation {
  provider: string;
  valid: boolean;
  error?: string;
}

export class ImageGenerationService {
  private static readonly BASE_URL = '/api/ImageGeneration';
  private static cachedProviders: ImageProvider[] | null = null;

  // Get available providers
  static async getProviders(): Promise<ImageProvider[]> {
    if (this.cachedProviders) {
      console.log('Returning cached providers:', this.cachedProviders);
      return this.cachedProviders;
    }
    
    try {
      console.log('Fetching providers from:', `${this.BASE_URL}/providers`);
      const response = await apiClient.get(`${this.BASE_URL}/providers`);
      console.log('Provider API response:', response.data);
      this.cachedProviders = response.data;
      return response.data;
    } catch (error) {
      console.error('Error fetching providers:', error);
      throw error;
    }
  }

  // Clear provider cache
  static clearProviderCache(): void {
    this.cachedProviders = null;
  }

  // Get all available models across all providers
  static async getAllModels(): Promise<ImageModel[]> {
    const providers = await this.getProviders();
    const allModels: ImageModel[] = [];
    
    providers.forEach(provider => {
      provider.models.forEach(model => {
        allModels.push({
          ...model,
          modelId: `${provider.name}:${model.modelId}` // Prefix with provider name for uniqueness
        });
      });
    });
    
    return allModels;
  }

  // Get model by ID
  static async getModel(modelId: string): Promise<ImageModel | null> {
    const providers = await this.getProviders();
    
    for (const provider of providers) {
      const model = provider.models.find(m => m.modelId === modelId);
      if (model) {
        return model;
      }
    }
    
    return null;
  }

  // Generate image with auto-provider selection
  static async generateImage(request: ImageGenerationRequest): Promise<ImageGenerationResponse> {
    const response = await apiClient.post(`${this.BASE_URL}/generate`, request);
    return response.data;
  }

  // Generate image with specific provider
  static async generateImageWithProvider(
    providerName: string, 
    request: ImageGenerationRequest
  ): Promise<ImageGenerationResponse> {
    const response = await apiClient.post(
      `${this.BASE_URL}/generate/${providerName}`, 
      request
    );
    return response.data;
  }

  // Validate provider configurations
  static async validateProviders(): Promise<ProviderValidation[]> {
    const response = await apiClient.get(`${this.BASE_URL}/validate`);
    return response.data;
  }

  // Helper to find the best size match for a model
  static findBestSizeMatch(model: ImageModel, targetWidth: number, targetHeight: number): ImageSize | null {
    if (!model.supportedSizes || model.supportedSizes.length === 0) {
      return null;
    }

    // Find exact match
    const exactMatch = model.supportedSizes.find(
      size => size.width === targetWidth && size.height === targetHeight
    );
    if (exactMatch) return exactMatch;

    // Find closest aspect ratio match
    const targetRatio = targetWidth / targetHeight;
    let closestSize = model.supportedSizes[0];
    let closestRatioDiff = Math.abs((closestSize.width / closestSize.height) - targetRatio);

    model.supportedSizes.forEach(size => {
      const sizeRatio = size.width / size.height;
      const ratioDiff = Math.abs(sizeRatio - targetRatio);
      if (ratioDiff < closestRatioDiff) {
        closestRatioDiff = ratioDiff;
        closestSize = size;
      }
    });

    return closestSize;
  }

  // Get provider name from model ID (if prefixed)
  static getProviderFromModelId(modelId: string): string | null {
    if (modelId.includes(':')) {
      return modelId.split(':')[0];
    }
    return null;
  }

  // Get clean model ID (without provider prefix)
  static getCleanModelId(modelId: string): string {
    if (modelId.includes(':')) {
      return modelId.split(':')[1];
    }
    return modelId;
  }

  // Download image
  static async downloadImage(imageUrl: string, filename?: string): Promise<void> {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `generated_image_${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading image:', error);
      throw error;
    }
  }
}